/**
 * Helpers for adding customizations to three.js shaders.
 * @module
 */
import { SSFXRendererOutput } from "../threejs/higharc/SSFXRendererOutput";
import type { DesignMaterialShaderChunks } from "./designMaterialShaderChunks";
import { ensureWorldPositionInFragShader } from "./shaders/ensureWorldPositionInFragShader";
import {
  entityIdHighlightDeclarationsFragment,
  entityIdHighlightDeclarationsVertex,
  entityIdHighlightFragment,
  entityIdHighlightVertex,
  entityIdHighlightVertexNoMix,
} from "./shaders/entityIdHighlight";
import { lightProbeGridAndMapsFragment, lightProbeGridParsFragment } from "./shaders/lightProbeGrid";
import { applySSAO, ssfxFetchFragment, ssfxParsFragment } from "./shaders/screenSpaceFX";
import { customShadowmapParsFragChunk } from "./shaders/softShadow";
import { safeShaderReplace, type ShaderProgramLike } from "./shaderUtils";

export function includeAdvancedPCFShadows(shader: ShaderProgramLike) {
  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <shadowmap_pars_fragment>",
    customShadowmapParsFragChunk,
    "includeAdvancedPCFShadows (shadowmap_pars_fragment)",
  );
}

export function includeCommonMaterialUniforms(shader: ShaderProgramLike) {
  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <common>",
    `uniform float randomSeed;
    #include <common>
    `,
    "includeCommonMaterialUniforms (common)",
  );
}

export function includeScreenspaceFX(shader: ShaderProgramLike) {
  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <common>",
    `#include <common>
    ${ssfxParsFragment}
    `,
    "includeScreenspaceFX (common)",
  );
  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <clipping_planes_fragment>",
    `#include <clipping_planes_fragment>
    ${ssfxFetchFragment}
    `,
    "includeScreenspaceFX (clipping_planes_fragment)",
  );
  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <aomap_fragment>",
    `#include <aomap_fragment>
    ${applySSAO}
    `,
    "includeScreenspaceFX (aomap_fragment)",
  );
}

export function designMaterialIncludeScreenSpaceFX(
  designMaterialShaderChunks: Pick<DesignMaterialShaderChunks, "fragmentDefinitions" | "clipping" | "aoModulation">,
) {
  designMaterialShaderChunks.fragmentDefinitions.push(ssfxParsFragment);
  designMaterialShaderChunks.clipping.push(ssfxFetchFragment);
  designMaterialShaderChunks.aoModulation.push(applySSAO);
}

export function includeLightProbes(shader: ShaderProgramLike) {
  ensureWorldPositionInFragShader(shader);
  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <common>",
    `#include <common>
    ${lightProbeGridParsFragment}
    `,
    "includeLightProbes (common)",
  );
  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <lights_fragment_maps>",
    `${lightProbeGridAndMapsFragment}
    `,
    "includeLightProbes (lights_fragment_maps)",
  );
}

export function designMaterialIncludeLightProbes(
  designMaterialShaderChunks: Pick<DesignMaterialShaderChunks, "fragmentDefinitions" | "lightsFragmentMaps">,
) {
  designMaterialShaderChunks.fragmentDefinitions.push(lightProbeGridParsFragment);
  designMaterialShaderChunks.lightsFragmentMaps = lightProbeGridAndMapsFragment;
}

export function includeEntityHighlight(shader: ShaderProgramLike, nomix = false) {
  shader.vertexShader = safeShaderReplace(
    shader.vertexShader,
    "#include <common>",
    `#include <common>
      ${entityIdHighlightDeclarationsVertex}
      `,
    "includeEntityHighlight vertex shader (common)",
  );
  shader.vertexShader = safeShaderReplace(
    shader.vertexShader,
    "#include <begin_vertex>",
    `#include <begin_vertex>
      ${nomix ? entityIdHighlightVertexNoMix : entityIdHighlightVertex}
      `,
    "includeEntityHighlight vertex shader (begin_vertex)",
  );
  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <common>",
    `#include <common>
      ${entityIdHighlightDeclarationsFragment}
      `,
    "includeEntityHighlight fragment shader (common)",
  );

  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <tonemapping_fragment>",
    `#include <tonemapping_fragment>
      ${entityIdHighlightFragment}
    `,
    "includeEntityHighlight fragment shader (tonemapping_fragment)",
  );
}

export function includeCameraFadeLayers(designMaterialShaderChunks: DesignMaterialShaderChunks) {
  designMaterialShaderChunks.fragmentDefinitions.push(
    `uniform float minCameraFadeDistance;
    uniform float invCameraFadeRange;`,
  );

  // We use a screen-door type pattern to make the transparency look good on low sample counts.
  designMaterialShaderChunks.clipping.push(
    `float cameraFadeAlpha = (length(vViewPosition) - minCameraFadeDistance) * (invCameraFadeRange);
    vec2 modFragCoord = mod(gl_FragCoord.xy, 2.0);
    if (cameraFadeAlpha < (modFragCoord.y * 2.0 + modFragCoord.x) * 0.25 + 0.125) {
      discard;
    }`,
  );
}

export function includeSsfxRendererOutput(shader: ShaderProgramLike) {
  // In case we're set to only render light, replace the material diffuse and specular colors with gray.
  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <lights_physical_fragment>",
    `#include <lights_physical_fragment>
    #if defined(SSFX_MATERIAL_DEV) && SSFX_MATERIAL_DEV == 1
    if (ssfxMaterialOutputType == ${SSFXRendererOutput.LIGHT.toFixed(
      0,
    )} || ssfxMaterialOutputType == ${SSFXRendererOutput.LIGHT_WITHOUT_DETAIL.toFixed(0)})
    {
        material.diffuseColor = vec3( 0.7 ) * ( 1.0 - metalnessFactor );
        material.diffuseContribution = vec3( 0.7 ) * ( 1.0 - metalnessFactor );
        material.specularColor = mix( vec3( 0.04 ), vec3( 0.7 ), metalnessFactor );
        material.specularColorBlended = mix( material.specularColor, vec3( 0.7 ), metalnessFactor );
    }
    #endif
    `,
    "includeSsfxRendererOutput (lights_physical_fragment)",
  );

  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <tonemapping_fragment>",
    `#include <tonemapping_fragment>
    #if defined(SSFX_MATERIAL_DEV) && SSFX_MATERIAL_DEV == 1
    if (ssfxMaterialOutputType == ${SSFXRendererOutput.LIGHT_PROBE_IRRADIANCE.toFixed(0)})
    {
        #if defined(LIGHT_PROBES_ENABLED) && LIGHT_PROBES_ENABLED == 1
            #ifndef PHYSICALLY_CORRECT_LIGHTS
                gl_FragColor = vec4( lightProbeIrradiance * RECIPROCAL_PI, 1.0 );
            #else
                gl_FragColor = vec4( lightProbeIrradiance, 1.0 );
            #endif
        #else
            gl_FragColor = vec4( 0.0, 0.0, 0.0, 1.0 );
        #endif
    }
    else if (ssfxMaterialOutputType == ${SSFXRendererOutput.BASE_COLOR.toFixed(0)})
    {
        gl_FragColor = vec4( diffuseColor.rgb, 1.0 );
    }
    #endif
    `,
    "includeSsfxRendererOutput (tonemapping_fragment)",
  );
}
